import * as React from "react";
import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Alert,
} from "@mui/material";
import { useApiClient } from "../../../api";

export const StudentEditorDialog = ({
  open,
  setOpen,
  student,
  yearGroups,
  onReload = () => {},
}) => {
  const ApiClient = useApiClient();

  const [identifier, setIdentifier] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [yearGroupId, setYearGroupId] = useState("");
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(null);

  useEffect(() => {
    if (student) {
      setIdentifier(student.studentIdentifier || "");
      setFirstName(student.firstName || "");
      setLastName(student.lastName || "");
      setYearGroupId(student.yearGroup?.id || "");
      setEmail(student.user?.email || "");
      setError(null);
    }
  }, [student]);

  const handleSubmit = async () => {
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      // TODO: email is still optional here?
      setError("Incorrect or missing email");
    }
    try {
      const response = await ApiClient.schools.updateStudent({
        student: student.id,
        studentIdentifier: identifier,
        firstName,
        lastName,
        yearGroup: yearGroupId,
        email: email,
      });

      if (response.success) {
        setOpen(false);
        onReload();
      } else {
        setError(response.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while updating the student.");
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const yearGroupChanged = student?.yearGroup?.id !== yearGroupId;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          sx: { backgroundImage: "none" },
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h4">Edit Student Details</Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Student Identifier"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            fullWidth
          />

          <TextField
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
          />

          <TextField
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
          />
          <FormControl>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              required
              fullWidth
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="year-group-label">Year Group</InputLabel>
            <Select
              labelId="year-group-label"
              value={yearGroupId}
              label="Year Group"
              onChange={(e) => setYearGroupId(e.target.value)}
            >
              {yearGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  Year {group.yearGroup}
                </MenuItem>
              ))}
            </Select>
            {yearGroupChanged && (
              <FormHelperText>
                Changing year group will remove the student from all current
                classes
              </FormHelperText>
            )}
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ mt: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !identifier.trim() ||
            !firstName.trim() ||
            !lastName.trim() ||
            !yearGroupId
          }
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
